import type { FirebaseOptions } from 'firebase/app'

export interface RawEnv {
  VITE_FIREBASE_API_KEY?: string
  VITE_FIREBASE_AUTH_DOMAIN?: string
  VITE_FIREBASE_PROJECT_ID?: string
  VITE_FIREBASE_STORAGE_BUCKET?: string
  VITE_FIREBASE_MESSAGING_SENDER_ID?: string
  VITE_FIREBASE_APP_ID?: string
  VITE_USE_EMULATOR?: string
}

export const EMULATOR_FALLBACK = { apiKey: 'demo-key', projectId: 'demo-notes' }

export function usingEmulator(env: RawEnv): boolean {
  return env.VITE_USE_EMULATOR === 'true'
}

export function readConfig(env: RawEnv): FirebaseOptions {
  return {
    apiKey: env.VITE_FIREBASE_API_KEY,
    authDomain: env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: env.VITE_FIREBASE_APP_ID,
  }
}

/** The emulator ignores credentials, so a placeholder project id is enough. */
export function isConfigured(env: RawEnv): boolean {
  const c = readConfig(env)
  return usingEmulator(env) || Boolean(c.apiKey && c.projectId)
}

/**
 * Resolves the options passed to initializeApp.
 *
 * Note the `||`, not `??`: an unset Vite variable arrives as an empty string,
 * which `??` happily keeps — that produced Firestore paths like
 * `projects//databases/(default)` and broke every request.
 */
export function resolveFirebaseOptions(env: RawEnv): FirebaseOptions {
  const config = readConfig(env)
  if (!usingEmulator(env)) return config
  return {
    ...config,
    apiKey: config.apiKey || EMULATOR_FALLBACK.apiKey,
    projectId: config.projectId || EMULATOR_FALLBACK.projectId,
  }
}
